import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import NumericInput from "@builder/components/general/NumericInput";
import TextInput from "@builder/components/general/TextInput";
import { getAppend, getPrepend, getSelectedNode, getFallbackText } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { TextNode } from "@common/build/types";
import { useEffect, useState } from "react";

interface TextDataProps extends EditableProps {
    noFallback?: boolean;
}

const TextData = ({ parent, noFallback }: TextDataProps) => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode) as TextNode;
    const [characterLimit, setLimit] = useState<number | string>(selectedNode.characterLimit || "");

    const append = useAppSelector(getAppend);
    const appendCallback = (value: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: ["append", "value"],
                value
            })
        );
    };

    const prepend = useAppSelector(getPrepend);
    const prependCallback = (value: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: ["prepend", "value"],
                value
            })
        );
    };

    const fallbackText = useAppSelector(getFallbackText);
    const fallbackTextCallback = (value: string) => {
        dispatch(
            updateNodeProperty({
                propertyPath: ["fallback", "value"],
                value
            })
        );
    };

    useEffect(() => {
        const parsedValue = parseFloat(characterLimit as string);
        const value = characterLimit === "" ? undefined : parsedValue;
        if (value !== undefined && value >= 0) {
            dispatch(
                updateNodeProperty({
                    propertyPath: ["characterLimit"],
                    value
                })
            );
        } else if (value === undefined) {
            dispatch(
                updateNodeProperty({
                    propertyPath: ["characterLimit"],
                    value: undefined
                })
            );
        }
    }, [characterLimit, dispatch]);

    useEffect(() => {
        setLimit(selectedNode?.characterLimit || "");
    }, [selectedNode?.characterLimit]);

    return (
        <SidebarCollapse title="Advanced Text Data" parent={parent}>
            <TextInput title="Prepend" value={prepend?.value} placeholder="Prepend text" callback={prependCallback} />
            <TextInput title="Append" value={append?.value} placeholder="Append text" callback={appendCallback} />
            {selectedNode?.dynamicData ? (
                <>
                    {!noFallback && (
                        <TextInput title="Fallback" value={fallbackText?.value} placeholder="Fallback text" callback={fallbackTextCallback} />
                    )}

                    <NumericInput title="Limit character" value={characterLimit} callback={setLimit} />
                </>
            ) : null}
        </SidebarCollapse>
    );
};

export default TextData;
